import React, { useState, useEffect } from 'react';
import { Row, Col, Container, Button, Card, Form } from 'react-bootstrap';
import { useAudit } from './AuditContext';
import AuditWidget from './AuditWidget';
import AuditInfoPanel from './AuditInfoPanel';
import { FeedbackMessage, AuditPageProps, ClientInfo } from './types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHeadset,
  faInfoCircle,
  faExclamationTriangle,
  faClipboardCheck,
  faComments,
  faTimesCircle,
  faArrowsRotate,
  faPhone,
  faEnvelope,
  faUserFriends,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import Avatar from 'components/base/Avatar';
import logoAuditCo from 'assets/img/icons/logo-auditco.png'; // Necesitarás crear/añadir este logo
import { auditService } from './audit-service';

/**
 * Main page for the audit document request system.
 * Organizes the interface into three columns: context information, conversation, and document panel.
 */
const AuditPage: React.FC<AuditPageProps> = ({
  title,
  agentId,
  agentName,
  agentImage,
  onError,
  onClose,
  clientName,
  clientCompany,
  clientCompanyInitials,
  clientPhone,
  clientEmail,
  firstMessage,
  prompt,
  voice,
  language
}) => {
  const {
    selectedClient,
    setSelectedClient,
    pendingDocuments,
    setPendingDocuments,
    sentDocuments,
    setSentDocuments,
    conversationStage,
    setConversationStage,
    systemMessages,
    addSystemMessage
  } = useAudit();

  // State to manage filtered system messages
  const [feedbackMessages, setFeedbackMessages] = useState<FeedbackMessage[]>(
    []
  );

  // Lista de clientes disponibles
  const [availableClients, setAvailableClients] = useState<ClientInfo[]>([]);

  // Estado local para el cliente seleccionado
  const [currentClientInfo, setCurrentClientInfo] = useState({
    name: clientName,
    company: clientCompany,
    companyInitials: clientCompanyInitials,
    phone: clientPhone,
    email: clientEmail
  });

  // Cargar los clientes disponibles al iniciar
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Cargar lista de clientes
        const clients = auditService.getClients();
        setAvailableClients(clients);

        // Buscar el cliente que coincida con las props o usar el primero
        const initialClient =
          clients.find(c => c.name === clientName) || clients[0];

        if (initialClient) {
          // Establecer el cliente seleccionado en el estado local
          setCurrentClientInfo({
            name: initialClient.name,
            company: initialClient.company,
            companyInitials: initialClient.companyInitials,
            phone: initialClient.phoneNumber,
            email: initialClient.email
          });

          // Establecer el cliente en el contexto
          setSelectedClient(initialClient);

          // Cargar explícitamente sus documentos
          setPendingDocuments(
            auditService.getPendingDocuments(initialClient.id)
          );
          setSentDocuments(auditService.getSentDocuments(initialClient.id));

          // Registrar la acción
          addSystemMessage(
            'info',
            `Cliente inicial: ${initialClient.name}`,
            `Empresa: ${initialClient.company}`
          );
        }
      } catch (error) {
        console.error('Error cargando datos iniciales:', error);
        addSystemMessage('error', 'Error cargando la lista de clientes');
      }
    };

    // Ejecutar la función de carga inicial
    loadInitialData();
  }, []);

  // Convert system messages to feedback messages
  useEffect(() => {
    const newFeedbackMessages = systemMessages.map(msg => ({
      id: msg.id,
      timestamp: msg.timestamp,
      type: msg.type as 'info' | 'document' | 'transfer' | 'close',
      message: msg.message,
      details: msg.details || '',
      success: msg.type !== 'error'
    }));

    setFeedbackMessages(newFeedbackMessages);
  }, [systemMessages]);

  // Manejador para cambiar de cliente
  const handleClientChange = (clientId: string) => {
    const newClient = availableClients.find(c => c.id === clientId);
    if (!newClient) return;

    // Actualizar el cliente seleccionado en el contexto
    setSelectedClient(newClient);

    // Actualizar la información local
    setCurrentClientInfo({
      name: newClient.name,
      company: newClient.company,
      companyInitials: newClient.companyInitials,
      phone: newClient.phoneNumber,
      email: newClient.email
    });

    // Cargar documentos del cliente seleccionado
    setPendingDocuments(auditService.getPendingDocuments(newClient.id));
    setSentDocuments(auditService.getSentDocuments(newClient.id));

    // Registrar el cambio
    addSystemMessage(
      'info',
      `Cliente cambiado a: ${newClient.name}`,
      `Empresa: ${newClient.company}`
    );
  };

  // Function to reset simulation
  const handleReset = () => {
    setConversationStage('identification');
    addSystemMessage('info', 'La simulación ha sido reiniciada');
  };

  // Render current status panel
  const renderStatusPanel = () => {
    const getStatusIcon = () => {
      switch (conversationStage) {
        case 'identification':
          return faHeadset;
        case 'document_listing':
          return faInfoCircle;
        case 'response':
          return faClipboardCheck;
        case 'finished':
          return faComments;
        default:
          return faHeadset;
      }
    };

    const getStatusText = () => {
      switch (conversationStage) {
        case 'identification':
          return 'Identificación';
        case 'document_listing':
          return 'Listado de documentos';
        case 'response':
          return 'Esperando respuesta';
        case 'finished':
          return 'Conversación finalizada';
        default:
          return 'En conversación';
      }
    };

    return (
      <div className="bg-body d-flex align-items-center border rounded mb-3 px-2 py-2">
        <div className="mx-3">
          <Avatar
            size="4xl"
            src={agentImage}
            placeholder={true}
            variant="image"
            rounded="circle"
            imageClassName="avatar-image-top shadow-sm border bg-body-emphasis"
          />
        </div>
        <div className="mx-4 text-center">
          <FontAwesomeIcon icon={getStatusIcon()} className="me-2 fs-6" />
          <div>
            <strong>{getStatusText()}</strong>
          </div>
        </div>
      </div>
    );
  };

  // Render client selector
  const renderClientSelector = () => {
    return (
      <div className="mb-3 mt-2">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">
            <FontAwesomeIcon
              icon={faUserFriends}
              className="me-2 text-primary"
            />
            Seleccionar Cliente
          </h5>
        </div>
        <Form.Select
          value={selectedClient?.id || ''}
          onChange={e => handleClientChange(e.target.value)}
          className="bg-body"
        >
          {availableClients.map(client => (
            <option key={client.id} value={client.id}>
              {client.name} - {client.company}
            </option>
          ))}
        </Form.Select>
      </div>
    );
  };

  // Render client data panel
  const renderClientPanel = () => {
    return (
      <div className="mb-4 fs-9">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">Datos del Cliente</h5>
        </div>
        <div className="p-3 border rounded bg-body">
          <div className="d-flex align-items-center mb-3">
            <Avatar
              size="2xl"
              placeholder={true}
              variant="name"
              rounded="soft"
              imageClassName="avatar-image-top shadow-sm border bg-body-emphasis"
            >
              {currentClientInfo.companyInitials}
            </Avatar>
            <div className="px-3 pe-0">
              <h6 className="mb-0">{currentClientInfo.name}</h6>
              <p className="mb-0 text-muted small">
                {currentClientInfo.company}
              </p>
            </div>
          </div>
          <p className="mb-1">
            <FontAwesomeIcon icon={faPhone} className="me-2 text-primary" />
            <strong>Teléfono:</strong> {currentClientInfo.phone}
          </p>
          <p className="mb-0">
            <FontAwesomeIcon icon={faEnvelope} className="me-2 text-primary" />
            <strong>Email:</strong> {currentClientInfo.email}
          </p>
          <div className="mt-2 pt-2 border-top">
            <h6>Documentos</h6>
            <div className="d-flex justify-content-between pt-2">
              <span>
                <FontAwesomeIcon
                  icon={faClipboardCheck}
                  className="me-2 text-danger"
                />
                <strong>Pendientes:</strong> {pendingDocuments.length}
              </span>
              <span>
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  className="me-2 text-success"
                />
                <span>Enviados:</span> {sentDocuments.length}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render system messages panel
  const renderFeedbackPanel = () => {
    return (
      <>
        <h6 className="pb-0 pt-2 mb-2">Mensajes del Sistema</h6>
        <div
          className="p-2 border rounded scrollbar bg-body-emphasis"
          style={{
            height: '218px',
            overflowY: 'auto',
            scrollBehavior: 'smooth'
          }}
        >
          {feedbackMessages.length === 0 ? (
            <p className="text-center text-muted fs-9 pt-4">
              No hay mensajes del sistema
            </p>
          ) : (
            feedbackMessages.map(msg => {
              const getIcon = () => {
                switch (msg.type) {
                  case 'info':
                    return faInfoCircle;
                  case 'document':
                    return faClipboardCheck;
                  case 'transfer':
                    return faHeadset;
                  case 'close':
                    return faTimesCircle;
                  default:
                    return faExclamationTriangle;
                }
              };

              const getColor = () => {
                return msg.success ? 'text-body-quaternary' : 'text-warning';
              };

              return (
                <div
                  key={msg.id}
                  className="alert bg-body p-2 mb-2 border rounded"
                >
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon
                      icon={getIcon()}
                      className={`me-2 ${getColor()}`}
                    />
                    <div className="d-flex flex-column flex-grow-1">
                      <small className="text-muted fs-10">
                        {msg.timestamp.toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        })}
                      </small>
                      <div className="fs-9 lh-1">{msg.message}</div>
                      {msg.details && (
                        <small className="mt-1 text-muted fs-10 lh-1">
                          {msg.details}
                        </small>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </>
    );
  };

  return (
    <div className="bg-body mt-n3">
      <Container fluid className="px-0">
        {/* Page Header & Breadcrumb */}
        <Row className="mb-3">
          <Col>
            <nav aria-label="breadcrumb" className="p-0 pb-1">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <a
                    href="#"
                    onClick={e => {
                      e.preventDefault();
                      if (onClose) {
                        onClose();
                      } else {
                        // Fallback if no close function
                        window.history.back();
                      }
                    }}
                  >
                    POCs
                  </a>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Gestión de Documentación
                </li>
              </ol>
            </nav>
            <div className="d-flex align-items-center mb-3">
              <h2 className="mb-0">
                {title || 'Sistema de Reclamación de Documentación'}
              </h2>
            </div>
          </Col>
        </Row>

        <Row className="g-3">
          {/* Left column: Context information */}
          <Col md={3}>
            <Card className="shadow-sm h-100">
              <Card.Body className="d-flex flex-column">
                <div className="text-center mb-2">
                  <img
                    src={logoAuditCo}
                    alt={`AuditCo Logo`}
                    width={130}
                    className="mb-3"
                  />
                </div>

                {renderStatusPanel()}

                {renderClientSelector()}

                {renderClientPanel()}

                <div className="mb-0 bg-body rounded border p-2">
                  <div className="mb-0 bg-body p-2">
                    <h6 className="pb-0">Instrucciones:</h6>
                    <ul className="mb-0 ps-3">
                      <li className="mb-1 small lh-sm">
                        El agente le informará sobre la documentación pendiente
                        y los plazos de entrega.
                      </li>
                      <li className="mb-1 small lh-sm">
                        Puede confirmar si ya ha enviado los documentos o
                        indicar cuándo podrá enviarlos.
                      </li>
                      <li className="mb-0 small lh-sm">
                        Si necesita más información, puede solicitar contactar
                        con un auditor humano.
                      </li>
                    </ul>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Center column: Conversation widget */}
          <Col md={4}>
            <Card className="shadow-sm h-100">
              <Card.Body className="d-flex flex-column">
                <div className="mb-3 border-bottom pb-3 d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-0">Llamada con {agentName}</h5>
                    <p className="text-muted small mb-0">
                      Pulse el botón para iniciar o finalizar la conversación
                    </p>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleReset}
                    title="Reiniciar simulación"
                    className="bg-body-quaternary pt-1 pb-0"
                  >
                    <FontAwesomeIcon icon={faArrowsRotate} />
                  </Button>
                </div>

                <div className="flex-grow-1 mb-3">
                  <AuditWidget
                    clientId={selectedClient?.id || ''}
                    clientName={currentClientInfo.name}
                    clientCompany={currentClientInfo.company}
                    agentId={agentId}
                    agentName={agentName}
                    onError={onError}
                    onFeedback={message =>
                      addSystemMessage(
                        message.type,
                        message.message,
                        message.details
                      )
                    }
                    firstMessage={firstMessage}
                    prompt={prompt}
                    voice={voice}
                    language={language}
                  />
                </div>

                {renderFeedbackPanel()}
              </Card.Body>
            </Card>
          </Col>

          {/* Right column: Document information panel */}
          <Col md={5}>
            <Card className="shadow-sm h-100">
              <Card.Body>
                <AuditInfoPanel />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AuditPage;
